import type { EtapaXabcde } from '../../dominio/didatico/cenariosExtracao'
import type { ProporcoesVitima } from '../../dominio/didatico/perfisVitima'
import { posicaoRegiaoAnatomica, type RegiaoAnatomica } from '../../dominio/didatico/regiaoAnatomica'
import { POSE_SENTADA, type ColocacaoVitima, type PoseVitima } from '../../dominio/didatico/rigVitima'
import { CORES_SEMANTICAS } from '../../dominio/didatico/tokens'
import { Rotulo3D } from '../componentes/Rotulo3D'

export interface AchadoDestacavel {
  etapa: EtapaXabcde
  resumo: string
  regiao: RegiaoAnatomica
}

interface Props {
  colocacao: ColocacaoVitima
  proporcoes: ProporcoesVitima
  pose?: PoseVitima
  achados: readonly AchadoDestacavel[]
}

/** separa achados empilhados na mesma região, pra não desenhar dois anéis um sobre o outro */
const OFFSET_MESMA_REGIAO_Y = 0.11

/**
 * Destaque discreto dos achados do XABCDE já revelados (Fase 5).
 *
 * Um anel fino e um rótulo curto — o mesmo resumo que já aparece no painel,
 * nunca o `detalhe`, e nada de geometria de ferimento. A posição vem de
 * `posicaoRegiaoAnatomica`, então acompanha a pose/colocação atuais da
 * vítima — a mesma fonte que já posiciona cabeça e ombros em outros lugares
 * da cena, não um offset desenhado à parte.
 *
 * A lista de `achados` já vem filtrada pelo domínio
 * (`achadosComRegiaoVisiveis`): este componente não decide o que está
 * revelado, só desenha o que chega pronto.
 */
export function DestaqueAchados3D({ colocacao, proporcoes, pose = POSE_SENTADA, achados }: Props) {
  if (achados.length === 0) return null

  const porRegiao = new Map<RegiaoAnatomica, AchadoDestacavel[]>()
  for (const achado of achados) {
    const lista = porRegiao.get(achado.regiao)
    if (lista) lista.push(achado)
    else porRegiao.set(achado.regiao, [achado])
  }

  return (
    <group>
      {[...porRegiao.entries()].flatMap(([regiao, achadosDaRegiao]) => {
        const centro = posicaoRegiaoAnatomica(regiao, colocacao, proporcoes, pose)
        return achadosDaRegiao.map((achado, indice) => (
          <group
            key={achado.etapa}
            position={[centro.x, centro.y + indice * OFFSET_MESMA_REGIAO_Y, centro.z]}
          >
            <mesh rotation={[Math.PI / 2, 0, 0]}>
              <torusGeometry args={[0.09, 0.011, 8, 24]} />
              <meshStandardMaterial
                color={CORES_SEMANTICAS.achado}
                emissive={CORES_SEMANTICAS.achado}
                emissiveIntensity={0.35}
                transparent
                opacity={0.85}
              />
            </mesh>
            <Rotulo3D posicao={{ x: 0, y: 0.16, z: 0 }} texto={`${achado.etapa} · ${achado.resumo}`} variante="achado" fator={4.5} />
          </group>
        ))
      })}
    </group>
  )
}
